// Gjøremål på startsiden: den brede knappen folder ut «Faste oppgaver» og «Dagens oppgaver»,
// og oppgavene kan krysses av rett her. Under listene: «Siste 10 fullførte» for alle, og
// «Legg til eller endre oppgaver» for leder (GjoremalRediger.jsx).
//
// Avkrysning skjer som på ToDo-siden (TodoList.js): først en rad i /completedtasks, så
// PATCH på oppgaven. Utvalget er også det samme, men regnet ut her uten å skrive noe:
//   faste   – alle for butikken; utført bare hvis krysset av i dag
//   dagens  – egne oppgaver med frist i dag eller før som ikke er gjort, og de som ble gjort i dag
//   senere  – egne oppgaver med senere frist, bare som antall per frist
import React, { useCallback, useEffect, useId, useState } from 'react';
import Cookies from 'js-cookie';
import confetti from 'canvas-confetti';
import { isAuthenticated } from '../auth';
import Ikon from './Ikon';
import GjoremalRediger, { datoVerdi } from './GjoremalRediger';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const iDag = (dato) => Boolean(dato) && new Date(dato).toDateString() === new Date().toDateString();
const klokke = (dato) => new Date(dato).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
const kortDato = (dato) => new Date(dato).toLocaleDateString('nb-NO', { day: 'numeric', month: 'numeric' });
const fristTekst = (dato) => new Date(dato).toLocaleDateString('nb-NO', { weekday: 'long', day: 'numeric', month: 'numeric' });
const lesButikk = () => parseInt(Cookies.get('butikkid'), 10) || null;
const iKveld = () => { const k = new Date(); k.setHours(23, 59, 59, 999); return k; };

function useOppgaver() {
  const [data, setData] = useState({ faste: [], egne: [], lastet: false });

  const hent = useCallback(async () => {
    if (!isAuthenticated()) return;
    const butikk = lesButikk();
    try {
      const [d, c] = await Promise.all([fetch(`${API_BASE_URL}/dailytasks`), fetch(`${API_BASE_URL}/customtasks`)]);
      if (!d.ok || !c.ok) return;
      const faste = (await d.json()).filter((t) => t.store === butikk);
      const egne = (await c.json()).filter((t) => t.store === butikk);
      setData({ faste, egne, lastet: true });
    } catch (e) {
      // Gjøremål er bare hjelp på startsiden; ToDo-siden viser feil.
    }
  }, []);

  useEffect(() => {
    hent();
    const id = setInterval(hent, 60000);
    window.addEventListener('storeUpdated', hent);
    return () => {
      clearInterval(id);
      window.removeEventListener('storeUpdated', hent);
    };
  }, [hent]);

  return [data, setData, hent];
}

function OppgaveRad({ oppgave, lagrer, onUtfor }) {
  if (oppgave.utfort) {
    return (
      <li className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 min-h-[2.75rem]">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
          <Ikon navn="hake" className="w-5 h-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-slate-600 line-through decoration-slate-400 [overflow-wrap:anywhere]">{oppgave.task}</span>
          <span className="block text-sm text-emerald-800">
            Utført{oppgave.completedBy ? ` av ${oppgave.completedBy}` : ''}{oppgave.dateCompleted ? ` kl. ${klokke(oppgave.dateCompleted)}` : ''}
          </span>
        </span>
      </li>
    );
  }
  return (
    <li>
      <button
        type="button"
        onClick={() => onUtfor(oppgave)}
        disabled={lagrer}
        aria-label={`Merk «${oppgave.task}» som utført`}
        className="group flex w-full items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-3 py-1.5 min-h-[2.75rem] text-left transition hover:border-emerald-300 hover:bg-emerald-50 active:scale-[0.99] touch-manipulation disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border-2 border-slate-400 bg-white text-emerald-700 group-hover:border-emerald-600">
          {lagrer && <span className="h-3 w-3 animate-pulse rounded-full bg-emerald-600" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-slate-900 [overflow-wrap:anywhere]">{oppgave.task}</span>
          {oppgave.forsinket && (
            <span className="block text-sm font-medium text-amber-800">Frist {kortDato(oppgave.dueDate)}</span>
          )}
        </span>
        <span className="hidden shrink-0 text-sm font-semibold text-emerald-800 sm:block">
          {lagrer ? 'Lagrer …' : 'Merk som utført'}
        </span>
      </button>
    </li>
  );
}

function Seksjon({ id, tittel, ikon, oppgaver, tomTekst, lagrer, onUtfor, children }) {
  const gjort = oppgaver.filter((o) => o.utfort).length;
  return (
    <section aria-labelledby={id} className="min-w-0">
      <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-700 text-white">
          <Ikon navn={ikon} className="w-6 h-6" />
        </span>
        <h3 id={id} className="text-base font-bold text-slate-900">{tittel}</h3>
        {oppgaver.length > 0 && (
          <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-medium text-slate-700">
            {gjort} av {oppgaver.length} gjort
          </span>
        )}
      </div>
      {oppgaver.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-600">{tomTekst}</p>
      ) : (
        <ul className="space-y-2">
          {oppgaver.map((o) => (
            <OppgaveRad key={o._id} oppgave={o} lagrer={lagrer === o._id} onUtfor={onUtfor} />
          ))}
        </ul>
      )}
      {children}
    </section>
  );
}

// Knapp som folder ut noe rett under seg (Siste 10 fullførte, Legg til eller endre oppgaver).
function Utfolding({ tittel, ikon, children }) {
  const id = useId();
  const [apen, setApen] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setApen((a) => !a)}
        aria-expanded={apen}
        aria-controls={id}
        className="flex w-full items-center gap-2 rounded-xl border-2 border-indigo-200 bg-white px-3 py-1.5 min-h-[2.75rem] text-left font-semibold text-indigo-900 transition hover:bg-indigo-50 active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-800">
          <Ikon navn={ikon} className="w-5 h-5" />
        </span>
        <span className="flex-1">{tittel}</span>
        <Ikon navn="ned" className={`w-6 h-6 shrink-0 text-indigo-700 transition-transform ${apen ? 'rotate-180' : ''}`} />
      </button>
      <div id={id} hidden={!apen} className="mt-3">
        {apen && children}
      </div>
    </div>
  );
}

function SisteFullforte({ butikk, versjon }) {
  const [liste, setListe] = useState(null);
  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/completedtasks?store=${butikk}`);
        if (!r.ok) throw new Error(String(r.status));
        const alle = await r.json();
        const siste = alle.sort((a, b) => new Date(b.dateCompleted) - new Date(a.dateCompleted)).slice(0, 10);
        if (aktiv) setListe(siste);
      } catch (e) {
        if (aktiv) setListe([]);
      }
    })();
    return () => { aktiv = false; };
  }, [butikk, versjon]);

  if (liste === null) return <p role="status" className="text-slate-600">Henter …</p>;
  if (liste.length === 0) return <p className="rounded-xl border border-dashed border-slate-300 px-3 py-1.5 text-slate-600">Ingen fullførte oppgaver ennå.</p>;
  return (
    <ol className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-white">
      {liste.map((t) => (
        <li key={t._id} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 px-3 py-1.5">
          <span className="min-w-0 flex-1 basis-56 font-medium text-slate-900 [overflow-wrap:anywhere]">{t.task}</span>
          <span className="text-sm text-slate-600">
            {t.employee} · {iDag(t.dateCompleted) ? 'i dag' : kortDato(t.dateCompleted)} kl. {klokke(t.dateCompleted)}
          </span>
        </li>
      ))}
    </ol>
  );
}

export default function Gjoremal({ handling, leder = false }) {
  const panelId = useId();
  const [apen, setApen] = useState(false);
  const [{ faste: alleFaste, egne, lastet }, setData, hent] = useOppgaver();
  const [lagrer, setLagrer] = useState(null);
  const [melding, setMelding] = useState('');
  const [feil, setFeil] = useState('');
  const [versjon, setVersjon] = useState(0); // øker når noe krysses av, så «Siste 10» henter på nytt

  const kveld = iKveld();
  const faste = alleFaste.map((t) => ({ ...t, type: 'daily', utfort: Boolean(t.completed && iDag(t.dateCompleted)) }));
  const dagens = egne
    .filter((t) => (t.dateCompleted ? iDag(t.dateCompleted) : new Date(t.dueDate) <= kveld))
    .map((t) => ({ ...t, type: 'custom', utfort: Boolean(t.dateCompleted), forsinket: !t.dateCompleted && !iDag(t.dueDate) }))
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  // Senere frister: antall per dag, f.eks. «2 oppgaver igjen med frist fredag 31.10.»
  const senere = Object.values(egne
    .filter((t) => !t.dateCompleted && new Date(t.dueDate) > kveld)
    .reduce((grupper, t) => {
      const dag = datoVerdi(t.dueDate);
      grupper[dag] = grupper[dag] || { dag, dato: t.dueDate, antall: 0 };
      grupper[dag].antall += 1;
      return grupper;
    }, {}))
    .sort((a, b) => a.dag.localeCompare(b.dag));

  const igjen = [...faste, ...dagens].filter((o) => !o.utfort).length;
  // Dagens utført-statistikk i banneret (vinnsel, 30.10): «Dagens 2 av 4», «Faste 3 av 5».
  const tall = [
    { navn: 'Dagens', gjort: dagens.filter((o) => o.utfort).length, av: dagens.length },
    { navn: 'Faste', gjort: faste.filter((o) => o.utfort).length, av: faste.length },
  ];
  const status = !lastet ? handling.hjelp
    : igjen === 0 ? 'Alt er gjort i dag'
      : `${igjen} ${igjen === 1 ? 'oppgave' : 'oppgaver'} igjen i dag`;

  const utfor = async (oppgave) => {
    const ansatt = Cookies.get('selectedEmployee') || '';
    setFeil('');
    if (!ansatt) {
      setFeil('Velg hvem du er øverst før du krysser av.');
      return;
    }
    setLagrer(oppgave._id);
    const naa = new Date().toISOString();
    try {
      const logg = await fetch(`${API_BASE_URL}/completedtasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task: oppgave.task,
          taskType: oppgave.type,
          ...(oppgave.type === 'custom' && { dueDate: oppgave.dueDate }),
          dateCompleted: naa,
          employee: ansatt,
          store: lesButikk(),
        }),
      });
      if (!logg.ok) throw new Error('completedtasks');

      const sti = oppgave.type === 'daily' ? 'dailytasks' : 'customtasks';
      const r = await fetch(`${API_BASE_URL}/${sti}/${oppgave._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed: true, completedBy: ansatt, dateCompleted: naa }),
      });
      if (!r.ok) throw new Error(sti);

      const merk = (liste) => liste.map((o) => (o._id === oppgave._id ? { ...o, completed: true, completedBy: ansatt, dateCompleted: naa } : o));
      setData((d) => ({ ...d, faste: merk(d.faste), egne: merk(d.egne) }));
      setVersjon((v) => v + 1);
      setMelding(`«${oppgave.task}» er merket som utført.`);
      confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 }, disableForReducedMotion: true });
    } catch (e) {
      setFeil('Kunne ikke lagre. Prøv igjen, eller bruk ToDo-siden.');
      hent();
    } finally {
      setLagrer(null);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setApen((a) => !a)}
        aria-expanded={apen}
        aria-controls={panelId}
        className={`flex w-full items-center gap-3 bg-indigo-700 p-3 sm:p-3 text-left text-white shadow-md transition hover:bg-indigo-800 active:scale-[0.99] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-400 ${apen ? 'rounded-t-2xl' : 'rounded-2xl'}`}
      >
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white text-indigo-700">
          <Ikon navn={handling.ikon} className="w-10 h-10" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-xl font-bold leading-tight">{handling.tittel}</span>
            {lastet && tall.map((t) => (
              <span
                key={t.navn}
                className={`rounded-full px-2.5 py-0.5 text-sm font-semibold ${t.av > 0 && t.gjort === t.av ? 'bg-emerald-400 text-emerald-950' : 'bg-indigo-600 text-white ring-1 ring-inset ring-indigo-300/60'}`}
              >
                {t.navn} {t.gjort} av {t.av}
              </span>
            ))}
          </span>
          <span className="mt-0.5 block text-base text-indigo-100">{status}</span>
        </span>
        {lastet && igjen > 0 && (
          <span className="flex h-10 min-w-[3rem] shrink-0 items-center justify-center rounded-full bg-white px-3 text-xl font-bold text-indigo-800" aria-hidden="true">
            {igjen}
          </span>
        )}
        <Ikon navn="ned" className={`w-6 h-6 shrink-0 text-indigo-200 transition-transform ${apen ? 'rotate-180' : ''}`} />
      </button>

      <div
        id={panelId}
        hidden={!apen}
        className="space-y-4 rounded-b-2xl border-2 border-t-0 border-indigo-200 bg-indigo-50/40 p-3 sm:p-3"
      >
        {feil && (
          <p role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-amber-900">{feil}</p>
        )}
        <p role="status" className="sr-only">{melding}</p>
        <div className="grid gap-3 lg:grid-cols-2">
          <Seksjon
            id={`${panelId}-faste`}
            tittel="Faste oppgaver"
            ikon="gjenta"
            oppgaver={faste}
            tomTekst="Ingen faste oppgaver for denne butikken."
            lagrer={lagrer}
            onUtfor={utfor}
          />
          <Seksjon
            id={`${panelId}-dagens`}
            tittel="Dagens oppgaver"
            ikon="kalender"
            oppgaver={dagens}
            tomTekst="Ingen oppgaver med frist i dag."
            lagrer={lagrer}
            onUtfor={utfor}
          >
            {senere.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {senere.map((g) => (
                  <li key={g.dag} className="flex items-center gap-2 text-slate-700">
                    <Ikon navn="kalenderKlokke" className="w-5 h-5 shrink-0 text-indigo-700" />
                    <span>
                      <strong className="font-semibold text-slate-900">{g.antall} {g.antall === 1 ? 'oppgave' : 'oppgaver'}</strong>
                      {' '}igjen med frist {fristTekst(g.dato)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Seksjon>
        </div>

        <div className="space-y-2 border-t border-indigo-200 pt-5">
          <Utfolding tittel="Siste 10 fullførte oppgaver" ikon="historikk">
            <SisteFullforte butikk={lesButikk()} versjon={versjon} />
          </Utfolding>
          {leder && (
            <Utfolding tittel="Legg til eller endre oppgaver" ikon="blyant">
              <GjoremalRediger faste={alleFaste} egne={egne} butikk={lesButikk()} onEndret={hent} />
            </Utfolding>
          )}
        </div>
      </div>
    </div>
  );
}
